import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

const dialogStack: symbol[] = [];
let bodyOverflowBeforeDialogs = "";

function getFocusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.tabIndex >= 0,
  );
}

interface DialogFocusOptions {
  enabled?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  restoreTarget?: HTMLElement | null;
}

export function useDialogFocus(
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  optionsOrInitialFocusRef:
    | DialogFocusOptions
    | RefObject<HTMLElement | null> = {},
) {
  const isLegacyInitialFocusRef = "current" in optionsOrInitialFocusRef;
  const options = isLegacyInitialFocusRef ? {} : optionsOrInitialFocusRef;
  const initialFocusRef = isLegacyInitialFocusRef
    ? optionsOrInitialFocusRef
    : options.initialFocusRef;
  const enabled = options.enabled ?? true;
  const restoreTargetOverride = options.restoreTarget;
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!enabled) return;

    const restoreTarget =
      restoreTargetOverride ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    const layer = Symbol();
    if (dialogStack.length === 0) {
      bodyOverflowBeforeDialogs = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    dialogStack.push(layer);
    const requestFrame =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame.bind(window)
        : (callback: FrameRequestCallback) =>
            window.setTimeout(() => callback(Date.now()), 16);
    const cancelFrame =
      typeof window.cancelAnimationFrame === "function"
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);

    const frame = requestFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog || dialogStack.at(-1) !== layer) return;
      if (
        document.activeElement instanceof HTMLElement &&
        dialog.contains(document.activeElement)
      ) {
        return;
      }
      const [firstFocusable] = getFocusableElements(dialog);
      (initialFocusRef?.current ?? firstFocusable ?? dialog).focus();
    });

    function handleKeyDown(event: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (!dialog || dialogStack.at(-1) !== layer) return;

      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;
      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstFocusable = focusableElements[0]!;
      const lastFocusable = focusableElements[focusableElements.length - 1]!;
      const activeElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      if (!activeElement || !dialog.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastFocusable : firstFocusable).focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelFrame(frame);
      window.removeEventListener("keydown", handleKeyDown);
      const wasTopLayer = dialogStack.at(-1) === layer;
      const layerIndex = dialogStack.indexOf(layer);
      if (layerIndex !== -1) dialogStack.splice(layerIndex, 1);
      if (dialogStack.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeDialogs;
      }
      if (
        wasTopLayer &&
        restoreTarget?.isConnected &&
        typeof restoreTarget.focus === "function"
      ) {
        restoreTarget.focus();
      }
    };
  }, [dialogRef, enabled, initialFocusRef, restoreTargetOverride]);
}
